/**
 * This is copied from `src/sections/workspace/components/page.tsx` but uses data
 * from redux that is provided by the global and/or section "view APIs", as well as input props
 * passed by consumers for the navigation component and the mobile header. Any changes made here
 * should be synced to that file as well.
 */
import {
  ComponentType,
  ReactNode,
  useEffect,
  useCallback,
  useRef,
  PropsWithChildren,
} from 'react';

import { useSelector } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import NavigationKeyboardShortcuts from 'src/sections/global/containers/navigation-keyboard-shortcuts';
import { NavigationFeedbackCollector } from 'src/sections/global/containers/navigation-feedback-collector';
import MobileHeader from 'src/sections/global/components/mobile-header';
import SiteBanner from 'src/sections/global/containers/site-banner';
import {
  getIsSiteMessageActive,
  getIsMobileHeaderActive,
} from 'src/selectors/global-selectors';
import * as consentBannerStyles from 'src/components/consent-banner.style';
import * as siteBannerStyles from 'src/components/site-banner.style';
import { ConsentBanner } from '@atlassian/browser-storage-controls';
import Page from './page';
import { SidebarProvider } from './sidebar';
import { useConsentBanner } from './navigation-provider';
import { useNavigationContext } from './navigation';

type Props = {
  children?: ReactNode;
  Component: ComponentType<PropsWithChildren<any>>;
  mobileHeading?: ReactNode | string;
  containerType?: string;
};

export const NavigationWrapper = (props: Props) => {
  const { Component, mobileHeading, children, containerType } = props;
  const { mobileHeading: navContextMobileHeading = '' } =
    useNavigationContext();
  const isMobileHeaderActive = useSelector(getIsMobileHeaderActive);
  const bannerRef = useRef(null);
  const [consentBannerState, consentBannerActions] = useConsentBanner();
  const isBannerOpen = useSelector(getIsSiteMessageActive);

  useEffect(() => {
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        let height = entry.contentRect.height;
        consentBannerActions.setConsentBannerHeight(height);
        // if the site banner is open we need to add the height
        // of the site banner in addition to the height of the consent banner
        if (isBannerOpen) {
          // site banner is 48
          height += 48;
        }
        // set the banner height for Nav V3 correctly
        const r = document.querySelector(':root');
        // @ts-ignore -- r is the root element
        r?.style?.setProperty('--bannerHeight', `${height}px`);
      }
    });

    if (bannerRef.current) {
      observer.observe(bannerRef.current);
    }

    return () => {
      if (bannerRef.current) {
        observer.unobserve(bannerRef.current);
      }
    };
  }, []);

  const renderNavigation = useCallback(
    () => <Component isBeingRenderedInsideMobileNav />,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // `SidebarProvider` is used by the main page and the mobile header, so needs to be
  // an ancestor of both.
  return (
    <ThemeProvider theme={{}}>
      <SidebarProvider>
        {isMobileHeaderActive && (
          <MobileHeader
            renderNavigation={renderNavigation}
            heading={mobileHeading || navContextMobileHeading}
            consentBannerHeight={consentBannerState.consentBannerHeight}
          />
        )}
        {isBannerOpen && (
          <siteBannerStyles.Wrapper
            topOffset={consentBannerState.consentBannerHeight}
          >
            <SiteBanner />
          </siteBannerStyles.Wrapper>
        )}
        <consentBannerStyles.Wrapper ref={bannerRef}>
          <ConsentBanner product="Bitbucket" />
        </consentBannerStyles.Wrapper>
        <Component
          isMobileHeaderActive={isMobileHeaderActive}
          containerType={containerType}
        >
          {/*
           * These children are not rendered when IndexV4 is rendered by the target "Component" (navigation-v2) /
           * when the bbc-navigation-restructure feature is enabled because IndexV4 renders NavigationKeyboardShortcuts,
           * the Page component, and UniversalRoutes directly.
           */}
          <NavigationKeyboardShortcuts>
            <Page>{children}</Page>
          </NavigationKeyboardShortcuts>
        </Component>
        <NavigationFeedbackCollector />
      </SidebarProvider>
    </ThemeProvider>
  );
};
